/** #704-C: bounded, read-only projection of the hosted AI context catalog. */
export interface AiContextRow {
  readonly ai_session_id: string;
  readonly project_id: string;
  readonly worker_number: number;
  readonly node_name: string;
  readonly creation_source: 'brunel_automated' | 'brunel_adopted' | 'owner_interactive' | 'imported';
  readonly runtime_key: string;
  readonly provider_identity_state: 'exact' | 'unknown' | 'ambiguous' | 'unavailable';
  readonly provider_session_id: string | null;
  readonly state: 'starting' | 'ready' | 'busy' | 'waiting_for_input' | 'retired' | 'error' | 'closed';
  readonly last_seen_at: string;
  readonly replaces_ai_session_id: string | null;
  readonly replacement_reason: string | null;
  readonly lineage_acknowledged: boolean | null;
  readonly disposition: 'keep' | 'disposable';
  readonly latest_assignment_id: string | null;
  readonly latest_task_id: string | null;
  readonly latest_task_title: string | null;
  readonly task_chain_id: string | null;
  readonly assignment_count: number;
  readonly display_title: string;
  readonly cleanup_state: AiContextCleanupState | null;
  readonly cleanup_detail: string | null;
}

/** #704-D: what the computer holding the conversation reported for an owner cleanup request. */
export type AiContextCleanupState = 'requested' | 'deleted' | 'archived' | 'owner_action_required' | 'unsupported' | 'failed';

export type AiContextFilter = 'all' | 'active' | 'automated' | 'owner' | 'archived' | 'disposable' | 'cleanup';

const SOURCES = new Set(['brunel_automated', 'brunel_adopted', 'owner_interactive', 'imported']);
const STATES = new Set(['starting', 'ready', 'busy', 'waiting_for_input', 'retired', 'error', 'closed']);
const IDENTITY = new Set(['exact', 'unknown', 'ambiguous', 'unavailable']);
const CLEANUP = new Set(['requested', 'deleted', 'archived', 'owner_action_required', 'unsupported', 'failed']);
const optionalText = (value: unknown): boolean => value === null || typeof value === 'string';

/** Reject a malformed read rather than silently dropping an observed context. */
export function parseAiContextRows(value: unknown, projectId: string): readonly AiContextRow[] | null {
  if (!Array.isArray(value)) return null;
  const rows: AiContextRow[] = [];
  for (const item of value) {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) return null;
    const row = item as Record<string, unknown>;
    if (typeof row.ai_session_id !== 'string' || typeof row.project_id !== 'string'
      || !Number.isInteger(row.worker_number) || (row.worker_number as number) < 1
      || typeof row.node_name !== 'string' || !SOURCES.has(String(row.creation_source))
      || typeof row.runtime_key !== 'string' || row.runtime_key === ''
      || !IDENTITY.has(String(row.provider_identity_state))
      || !optionalText(row.provider_session_id)
      || (row.provider_identity_state === 'exact' && row.provider_session_id === null)
      || !STATES.has(String(row.state)) || typeof row.last_seen_at !== 'string'
      || !optionalText(row.replaces_ai_session_id) || !optionalText(row.replacement_reason)
      || !(row.lineage_acknowledged === null || typeof row.lineage_acknowledged === 'boolean')
      || !['keep', 'disposable'].includes(String(row.disposition))
      || !optionalText(row.latest_assignment_id) || !optionalText(row.latest_task_id)
      || !optionalText(row.latest_task_title) || !optionalText(row.task_chain_id)
      || !Number.isInteger(row.assignment_count) || (row.assignment_count as number) < 0
      || typeof row.display_title !== 'string'
      || !(row.cleanup_state === null || CLEANUP.has(String(row.cleanup_state)))
      || !optionalText(row.cleanup_detail)) return null;
    if (row.project_id === projectId) rows.push(row as unknown as AiContextRow);
  }
  return rows;
}

export function aiContextMatches(row: AiContextRow, filter: AiContextFilter): boolean {
  switch (filter) {
    case 'all': return true;
    case 'active': return ['starting', 'ready', 'busy', 'waiting_for_input'].includes(row.state);
    case 'automated': return row.creation_source === 'brunel_automated' || row.creation_source === 'brunel_adopted';
    case 'owner': return row.creation_source === 'owner_interactive';
    case 'archived': return row.state === 'retired' || row.state === 'closed';
    case 'disposable': return row.disposition === 'disposable';
    case 'cleanup': return row.cleanup_state !== null;
  }
}

/** The owner may ask only for an ended, Disposable, Brunel-created context with an exact native ID. */
export function aiContextCleanupRequestable(row: AiContextRow): boolean {
  return (row.creation_source === 'brunel_automated' || row.creation_source === 'brunel_adopted')
    && row.disposition === 'disposable'
    && row.provider_identity_state === 'exact' && row.provider_session_id !== null
    && ['retired', 'closed', 'error'].includes(row.state)
    && (row.cleanup_state === null || row.cleanup_state === 'failed');
}

export function aiContextCleanupLabel(state: AiContextCleanupState | null): string {
  switch (state) {
    case null: return 'Not requested';
    case 'requested': return 'Requested — waiting for its computer';
    case 'deleted': return 'Deleted in the runtime';
    case 'archived': return 'Archived in the runtime';
    case 'owner_action_required': return 'Owner action required';
    case 'unsupported': return 'Unsupported by this runtime';
    case 'failed': return 'Failed';
  }
}

export function aiContextCounts(rows: readonly AiContextRow[]): Readonly<Record<AiContextFilter, number>> {
  const filters: readonly AiContextFilter[] = ['all', 'active', 'automated', 'owner', 'archived', 'disposable', 'cleanup'];
  return Object.fromEntries(filters.map((filter) => [filter, rows.filter((row) => aiContextMatches(row, filter)).length])) as Readonly<Record<AiContextFilter, number>>;
}
